/* ============================================================
   MsDressly Motion Layer — vanilla JS animation engine
   - Reveal-on-scroll via IntersectionObserver (once-only)
   - Hero line-mask entrance, rolling word, scroll parallax
   - No dependencies. Honors prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supportsIO = 'IntersectionObserver' in window;

  /* ─── IntersectionObserver reveal engine ─────────────────── */
  var revealIO = supportsIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      el.classList.add('msd-in');
      revealIO.unobserve(el);
      el.addEventListener('transitionend', function handler() {
        el.classList.add('msd-done');
        el.removeEventListener('transitionend', handler);
      });
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }) : null;

  function reveal(el, index) {
    /* Fail-open: without IO or with reduced motion, never hide content. */
    if (!supportsIO || reduceMotion) { el.classList.add('msd-in'); return; }
    el.setAttribute('data-reveal', '');
    if (index) el.style.setProperty('--msd-i', index);
    el.classList.add('msd-armed');
    revealIO.observe(el);
  }

  /* Stagger matching items within each group container */
  function stagger(itemSelector, groupSelector, max) {
    document.querySelectorAll(groupSelector).forEach(function (group) {
      var items = group.querySelectorAll(itemSelector);
      var i = 0;
      items.forEach(function (el) {
        if (i >= max) return;
        reveal(el, i);
        i++;
      });
    });
  }

  function initReveals() {
    // Value props
    stagger('.msd-value-props__item', '.msd-value-props__grid', 6);
    document.querySelectorAll('.msd-value-props__copy').forEach(function (el) { reveal(el, 0); });
    // Product cards, per collection section
    stagger('.product-collection', '.sorting-collections', 8);
    // Promoboxes, per builder section
    stagger('.promobox', '.home-builder', 4);
    // Instagram builder block
    document.querySelectorAll('.builder-instagram').forEach(function (el) { reveal(el, 0); });
  }

  /* ─── Hero: line-mask entrance + staged reveals ──────────── */
  function initHero() {
    var hero = document.querySelector('.msd-luxury-hero');
    if (!hero) return;

    /* Do not rewrite heading HTML after paint — that shifts the LCP text. */
    var staged = [
      hero.querySelector('.msd-luxury-hero__eyebrow'),
      hero.querySelector('.msd-luxury-hero__roller'),
      hero.querySelector('.msd-luxury-hero__body'),
      hero.querySelector('.msd-luxury-hero__actions'),
      hero.querySelector('.msd-luxury-hero__stats')
    ].filter(Boolean);
    staged.forEach(function (el, i) {
      reveal(el, i + 1);
    });

    if (!supportsIO || reduceMotion) { hero.classList.add('msd-in'); return; }
    hero.classList.add('msd-armed');
    revealIO.observe(hero);
  }

  /* ─── Rolling word ───────────────────────────────────────── */
  function initRoller() {
    document.querySelectorAll('[data-msd-roller]').forEach(function (el) {
      var wordEl = el.querySelector('[data-msd-roller-word]');
      if (!wordEl) return;
      var words = el.getAttribute('data-msd-roller')
        .split(',')
        .map(function (w) { return w.trim(); })
        .filter(Boolean);
      if (words.length < 2) return;
      if (reduceMotion) {
        wordEl.textContent = words[0];
        return;
      }

      var i = 0;
      var timer = null;
      var hovered = false;

      function next() {
        wordEl.classList.add('msd-roll-out');
        setTimeout(function () {
          i = (i + 1) % words.length;
          wordEl.textContent = words[i];
          wordEl.classList.remove('msd-roll-out');
          wordEl.classList.add('msd-roll-in');
          setTimeout(function () { wordEl.classList.remove('msd-roll-in'); }, 600);
        }, 460);
      }
      function start() { if (!timer) timer = setInterval(function () { if (!hovered) next(); }, 2800); }
      function stop() { clearInterval(timer); timer = null; }

      el.addEventListener('mouseenter', function () { hovered = true; });
      el.addEventListener('mouseleave', function () { hovered = false; });
      new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) start(); else stop();
      }, { threshold: 0.1 }).observe(el);
    });
  }

  /* ─── Hero copy parallax (editorial drift on scroll) ─────── */
  function initParallax() {
    if (reduceMotion) return;
    var hero = document.querySelector('.msd-luxury-hero');
    var copy = hero && hero.querySelector('.msd-luxury-hero__copy');
    if (!hero || !copy) return;
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    var active = false;
    var ticking = false;
    new IntersectionObserver(function (entries) {
      active = entries[0].isIntersecting;
    }, { threshold: 0 }).observe(hero);

    window.addEventListener('scroll', function () {
      if (!active || ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var rect = hero.getBoundingClientRect();
        var progress = Math.min(Math.max(-rect.top / rect.height, 0), 1);
        copy.style.transform = 'translateY(' + (progress * -4).toFixed(2) + '%)';
        copy.style.opacity = (1 - progress * 0.15).toFixed(3);
        ticking = false;
      });
    }, { passive: true });
  }

  /* ─── Safety sweep: force-reveal anything still armed ──────
     If IO callbacks are delayed (slow load, bfcache, throttled
     tabs), never leave content hidden — reveal it after a grace
     period. Armed-but-hidden state is transition-only, so this
     is a no-op for elements already revealed. */
  function sweep() {
    document.querySelectorAll('.msd-armed:not(.msd-in)').forEach(function (el) {
      el.classList.add('msd-in');
    });
  }

  function scheduleSweep() {
    if (!supportsIO || reduceMotion) return;
    window.addEventListener('load', function () { setTimeout(sweep, 3500); });
    setTimeout(sweep, 7000);
  }

  /* ─── Boot ───────────────────────────────────────────────── */
  function init() {
    initReveals();
    initHero();
    initRoller();
    initParallax();
    scheduleSweep();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
